import { useEffect, useState } from 'react'
import {
  useController,
  UseControllerProps,
  FieldValues,
  FieldPath,
} from 'react-hook-form'
import { IconType } from 'react-icons'

import { RadioGroup } from '@headlessui/react'

interface RadioInterface {
  icon?: IconType
  value: number | string
  title: string
  description: string
}

interface RadioProps {
  label?: string
  disabled?: boolean
  data: RadioInterface[]
  action?: () => void
}

function classNames(...classes: string[]) {
  return classes.filter(Boolean).join(' ')
}

export default function RadioGroupForm<
  TFieldValues extends FieldValues,
  TName extends FieldPath<TFieldValues>,
>(props: UseControllerProps<TFieldValues, TName> & RadioProps) {
  const { data, label, defaultValue, action } = props
  const { field } = useController(props)

  const [radioValue, setRadioValue] = useState<RadioInterface | null>(null)

  useEffect(() => {
    const index = data.findIndex(
      (item: RadioInterface) =>
        item.value === defaultValue || item.value === field.value
    )
    if (index !== -1) setRadioValue(data[index])
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const onChange = (e: RadioInterface) => {
    setRadioValue(e)
    field.onChange(e.value)
    if (action) {
      action()
    }
  }

  return (
    <RadioGroup onChange={onChange} value={radioValue}>
      <RadioGroup.Label className="text-base font-semibold leading-6 text-gray-900">
        {label}
      </RadioGroup.Label>

      <div className="mt-4 grid grid-cols-1 gap-y-6 sm:grid-cols-2 sm:gap-x-4">
        {data.map((item) => (
          <RadioGroup.Option
            className={({ checked, active }) =>
              classNames(
                checked ? 'border-transparent' : 'border-gray-300',
                active ? 'border-slate-600 ring-2 ring-slate-600' : '',
                'relative flex cursor-pointer rounded-lg border bg-white p-6 shadow-sm focus:outline-none'
              )
            }
            key={item.value}
            value={item}
          >
            {({ checked, active }) => (
              <>
                <span className="flex flex-1">
                  <span className="flex flex-col">
                    <RadioGroup.Label
                      as="span"
                      className="flex items-center gap-2 text-lg font-medium text-gray-600"
                    >
                      {item.icon && <item.icon className="text-gray-400" />}
                      {item.title}
                    </RadioGroup.Label>
                    <RadioGroup.Description
                      as="span"
                      className="text-md mt-1 flex items-center text-gray-500"
                    >
                      {item.description}
                    </RadioGroup.Description>
                  </span>
                </span>
                <span
                  aria-hidden="true"
                  className={classNames(
                    active ? 'border' : 'border-2',
                    checked ? 'border-slate-500' : 'border-transparent',
                    'pointer-events-none absolute -inset-px rounded-lg'
                  )}
                />
              </>
            )}
          </RadioGroup.Option>
        ))}
      </div>
    </RadioGroup>
  )
}
